import React, { useState, useEffect, useContext } from "react";
import { TextField, Box, Typography } from "@mui/material";
import ApexCharts from "react-apexcharts"; // Importamos ApexCharts
import { ApexOptions } from "apexcharts";
import { AuthContext } from "../../context/AuthContext";


interface CourseData {
  group_name: string;
  total_students: number;
}

const StudentsByCourse: React.FC = () => {
  const { token } = useContext(AuthContext) || {};

  const currentDate = new Date();
  const currentMonthYear = `${currentDate.getFullYear()}-${(
    currentDate.getMonth() + 1
  )
    .toString()
    .padStart(2, "0")}`;

  const [monthYear, setMonthYear] = useState<string>(currentMonthYear);
  const [studentsData, setStudentsData] = useState<CourseData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleMonthYearChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setMonthYear(event.target.value);
  };

  const fetchData = async () => {
    let options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };

    if (!monthYear) {
      setError("Por favor selecciona un mes y año.");
      return;
    }

    setLoading(true);
    setError(null);

    const [year, month] = monthYear.split("-");

    try {
      const response = await fetch(
        `/api/students/studentsenrollingbymonth?month=${month}&year=${year}`,
        options
      );

      if (!response.ok) {
        throw new Error("No se pudieron obtener los datos.");
      }

      const data = await response.json();
      setStudentsData(data.data || []);
    } catch (error: unknown) {
      setError((error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthYear, token]);

  const chartData = {
    series: [
      {
        name: "Estudiantes Inscritos",
        data: studentsData.map((course) => course.total_students),
      },
    ],
    categories: studentsData.map((course) => course.group_name),
  };

  const options: ApexOptions = {
    chart: {
      type: "bar",
      height: "350",
    },
    xaxis: {
      categories: chartData.categories,
    },
    title: {
      text: "Estudiantes Inscritos por Curso",
      style: {
        fontSize: "20px",
        fontWeight: "bold",
      },
    },
    yaxis: {
      title: {
        text: "Número de Estudiantes",
      },
    },
    fill: {
      colors: ["rgba(74, 240, 167, 0.6)"],
    },
    dataLabels: {
      style: {
        fontSize: "14px",
        fontWeight: "bold",
        colors: ["#151515"],
      },
    },
  };

  return (
    <Box sx={{ padding: 2, minHeight: "350px" }}>
      <Typography variant="h4" gutterBottom>
        Estudiantes Inscritos por Curso
      </Typography>

      <TextField
        label="Seleccionar Mes y Año"
        type="month"
        value={monthYear}
        onChange={handleMonthYearChange}
        variant="outlined"
        fullWidth
        margin="normal"
        InputLabelProps={{ shrink: true }}
      />

      {error && (
        <Typography color="error" variant="body1" sx={{ marginTop: 2 }}>
          Error: {error}
        </Typography>
      )}

      {/* No mostrar la gráfica si no hay datos */}
      {!loading && studentsData.length > 0 && (
        <Box sx={{ marginTop: 4 }}>
          <ApexCharts
            options={options}
            series={chartData.series}
            type="bar"
            height={350}
          />
        </Box>
      )}

      {/* Mensaje cuando no hay resultados */}
      {!loading && studentsData.length === 0 && (
        <Typography variant="body1" sx={{ marginTop: 2 }}>
          No se encontraron estudiantes para el mes y año seleccionados.
        </Typography>
      )}
    </Box>
  );
};

export default StudentsByCourse;
